import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

const testimonials = [
  { name: 'Maria G.', role: 'Casa Verde Restaurant', quote: 'SiteStackCo transformed how we look online. We went from no web presence to daily calls from our new site within the first week.', initial: 'M', rating: 5 },
  { name: 'James T.', role: 'Thornton & Associates Law', quote: 'Professional, fast, and they actually listened. Our new site projects exactly the trust and authority we wanted.', initial: 'J', rating: 5 },
  { name: 'Destiny W.', role: 'DW Fitness Studio', quote: 'The photography session alone was worth every penny. Our Instagram looks incredible and we had to hire two new trainers to keep up.', initial: 'D', rating: 5 },
  { name: 'Kevin P.', role: "Park's Auto Detail", quote: 'I was skeptical about investing in marketing, but after 90 days I had to pause the ads because I was fully booked out.', initial: 'K', rating: 5 },
];

const Testimonials: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (trackRef.current?.offsetLeft || 0));
    setScrollLeft(trackRef.current?.scrollLeft || 0);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !trackRef.current) return;
    const x = e.pageX - trackRef.current.offsetLeft;
    trackRef.current.scrollLeft = scrollLeft - (x - startX);
  };
  const onMouseUp = () => setIsDragging(false);

  return (
    <section id="testimonials" className="py-32 bg-[#080808] border-t border-[#0f0f0f]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22,1,0.36,1] }}
          >
            <p className="section-label mb-5">What Clients Say</p>
            <h2 className="font-serif text-[clamp(2.2rem,5vw,4.5rem)] text-white leading-tight">
              Real Words From{' '}
              <span className="italic text-lime">Real Clients</span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-3"
          >
            <span className="font-syne text-xs text-[#888] uppercase tracking-widest">Drag to scroll</span>
            <svg className="w-4 h-4 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.div>
        </div>

        {/* Scrollable row */}
        <div
          ref={trackRef}
          className="flex gap-4 overflow-x-auto pb-4 cursor-grab active:cursor-grabbing select-none"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6, ease: [0.22,1,0.36,1] }}
              className="group flex-shrink-0 w-[340px] border border-[#141414] rounded-2xl p-7 relative overflow-hidden"
              whileHover={{ borderColor: '#C8F13530', y: -4 }}
            >
              {/* Glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(circle at 20% 20%, rgba(200,241,53,0.04), transparent 60%)' }} />

              {/* Stars */}
              <div className="flex gap-0.5 mb-5">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <span key={i} className="text-lime text-sm">★</span>
                ))}
              </div>

              <p className="font-serif text-[1.05rem] text-[#ccc] leading-relaxed mb-7 italic">
                "{t.quote}"
              </p>

              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-lime flex items-center justify-center flex-shrink-0">
                  <span className="font-syne font-bold text-[#080808] text-sm">{t.initial}</span>
                </div>
                <div>
                  <p className="font-syne font-bold text-white text-sm">{t.name}</p>
                  <p className="font-dm text-[#888] text-xs mt-0.5">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}

          {/* CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex-shrink-0 w-[280px] border border-dashed border-[#1e1e1e] rounded-2xl p-7 flex flex-col items-center justify-center text-center gap-5"
          >
            <div className="w-14 h-14 rounded-full bg-lime/10 border border-lime/20 flex items-center justify-center">
              <span className="text-lime text-xl">+</span>
            </div>
            <div>
              <p className="font-syne font-bold text-white text-sm mb-1">Your Story Next?</p>
              <p className="font-dm text-[#888] text-xs leading-relaxed">Join our growing list of successful clients.</p>
            </div>
            <a href="#contact" className="btn-lime px-5 py-2.5 text-xs">Get Started</a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
